import { IMosaicTocItemUpdate, MosaicTocBaseDto } from "./interface";

/**
 * Base class for Table of Contents items.
 */
export class MosaicTocBaseClass implements MosaicTocBaseDto {
  protected _id = "";

  /**
   * Gets the unique identifier for this TOC item.
   */
  get id() {
    return this._id;
  }

  protected _changed = false;

  /**
   * Gets whether this item has unsaved changes.
   */
  get changed() {
    return this._changed;
  }

  protected _inError = false;

  /**
   * Gets whether this item has a save error.
   */
  get inError() {
    return this._inError;
  }

  protected _primaryHierarchyId = "";

  /**
   * Gets the primary hierarchy ID for this item.
   */
  get primaryHierarchyId() {
    return this._primaryHierarchyId;
  }

  protected _updateTimestamp: number | null = null;

  /**
   * Gets the timestamp of the last update.
   */
  get updateTimestamp() {
    return this._updateTimestamp;
  }

  protected _tocNumber = "";

  /**
   * Gets the TOC number (e.g., "1.2.3").
   */
  get tocNumber() {
    return this._tocNumber;
  }

  protected _tocLabel = "";

  /**
   * Gets the TOC label text.
   */
  get tocLabel() {
    return this._tocLabel;
  }

  protected _tocOrder = -1;

  /**
   * Gets the sort order for this TOC item.
   */
  get tocOrder() {
    return this._tocOrder;
  }

  /**
   * Creates a new TOC base class instance.
   *
   * @param input - The TOC base data to initialise from
   */
  constructor(input: MosaicTocBaseDto) {
    const errCheck: string[] = [];
    if (input.id.trim().length === 0) {
      errCheck.push("mosaicTocItem: contructor: id cannot be blank");
    } else if (input.id.match(/^[A-Za-z0-9-_]+$/) === null) {
      errCheck.push(
        `mosaicTocItem: contructor: id (${input.id}) must contain alphanumerics, hyphen and underscore`,
      );
    }
    if (errCheck.length > 0) {
      throw errCheck.join("\n");
    }

    this._id = input.id;
    this._primaryHierarchyId = input.primaryHierarchyId;
    this._tocNumber =
      input.tocNumber ??
      // istanbul ignore next
      "";
    this._tocLabel =
      input.tocLabel ??
      // istanbul ignore next
      "";
    this._updateTimestamp = new Date().getTime();
    this._tocOrder =
      input.tocOrder ??
      // istanbul ignore next
      -1;
    this._changed = input.changed;
    this._inError = input.inError;
  }

  /**
   * Marks this item as successfully saved.
   */
  public markSaved() {
    this._changed = false;
    this._inError = false;
    this._updateTimestamp = new Date().getTime();
  }

  /**
   * Marks this item as having a save failure.
   */
  public markSaveFailed() {
    this._changed = true;
    this._inError = true;
    this._updateTimestamp = new Date().getTime();
  }

  /**
   * Updates this TOC item with new values.
   *
   * @param update - The update data to apply
   * @param now - Optional. The timestamp for this update
   */
  public update(update: IMosaicTocItemUpdate, now: number | null = null) {
    if (update.id !== this._id) {
      throw new Error("Cannot update a section with a different id");
    }
    now = now ?? new Date().getTime();
    if (
      update.primaryHierarchyId !== undefined &&
      this._primaryHierarchyId !== update.primaryHierarchyId
    ) {
      this._primaryHierarchyId = update.primaryHierarchyId;
      this._changed = !update.saved;
      this._inError = false;
      this._updateTimestamp = now;
    }
    if (update.tocNumber !== undefined && this._tocNumber !== update.tocNumber) {
      this._tocNumber = update.tocNumber;
      this._changed = !update.saved;
      this._inError = false;
      this._updateTimestamp = now;
    }
    if (update.tocLabel !== undefined && this._tocLabel !== update.tocLabel) {
      this._tocLabel = update.tocLabel;
      this._changed = !update.saved;
      this._inError = false;
      this._updateTimestamp = now;
    }
    if (update.tocOrder !== undefined && this._tocOrder !== update.tocOrder) {
      this._tocOrder = update.tocOrder;
      this._changed = !update.saved;
      this._inError = false;
      this._updateTimestamp = now;
    }
  }
}
